/**
 * TransitTimeMap - Interactive US map for transit time visualization
 * Uses react-simple-maps for proper geographic rendering
 * Uses ship points from tenant Settings as origins
 */

import React, { memo, useMemo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line
} from 'react-simple-maps';

// US Atlas TopoJSON URL
const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Location code to coordinates mapping (common US locations)
export const LOCATION_COORDINATES = {
  // Major cities by common codes
  'BOS': [-71.0589, 42.3601],    // Boston
  'LA': [-118.2437, 34.0522],    // Los Angeles
  'NY': [-74.0060, 40.7128],     // New York
  'CHI': [-87.6298, 41.8781],    // Chicago
  'DAL': [-96.7970, 32.7767],    // Dallas
  'ATL': [-84.3880, 33.7490],    // Atlanta
  'DEN': [-104.9903, 39.7392],   // Denver
  'PHX': [-112.0740, 33.4484],   // Phoenix
  'MIA': [-80.1918, 25.7617],    // Miami
  'SEA': [-122.3321, 47.6062],   // Seattle
  'MEM': [-90.0490, 35.1495],    // Memphis
  'KC': [-94.5786, 39.0997],     // Kansas City
  'SF': [-122.4194, 37.7749],    // San Francisco
  'HOU': [-95.3698, 29.7604],    // Houston
  'PHI': [-75.1652, 39.9526],    // Philadelphia
  'SLC': [-111.8910, 40.7608],   // Salt Lake City
  'MSP': [-93.2650, 44.9778],    // Minneapolis
  'DET': [-83.0458, 42.3314],    // Detroit
  'STL': [-90.1994, 38.6270],    // St. Louis
  'POR': [-122.6765, 45.5152],   // Portland
  'LV': [-115.1398, 36.1699],    // Las Vegas
  'SD': [-117.1611, 32.7157],    // San Diego
  'ORL': [-81.3792, 28.5383],    // Orlando
  'CLE': [-81.6944, 41.4993],    // Cleveland
  'PIT': [-79.9959, 40.4406],    // Pittsburgh
  'CIN': [-84.5120, 39.1031],    // Cincinnati
  'IND': [-86.1581, 39.7684],    // Indianapolis
  'NAS': [-86.7816, 36.1627],    // Nashville
  'JAX': [-81.6557, 30.3322],    // Jacksonville
  'OKC': [-97.5164, 35.4676],    // Oklahoma City
  'SAN': [-98.4936, 29.4241],    // San Antonio
  'AUS': [-97.7431, 30.2672],    // Austin
};

// Walmart DC locations (common destinations)
export const WALMART_DCS = {
  '6094': { name: 'DC 6094', city: 'Bentonville, AR', coordinates: [-94.2088, 36.3729], type: 'DC' },
  '7012': { name: 'DC 7012', city: 'Brooksville, FL', coordinates: [-82.3879, 28.5553], type: 'DC' },
  '6043': { name: 'DC 6043', city: 'Cleburne, TX', coordinates: [-97.3867, 32.3476], type: 'DC' },
  '6022': { name: 'DC 6022', city: 'Shelbyville, TN', coordinates: [-86.4603, 35.4834], type: 'DC' },
  '6080': { name: 'DC 6080', city: 'Gas City, IN', coordinates: [-85.6136, 40.4875], type: 'DC' },
  '6090': { name: 'DC 6090', city: 'Bethlehem, PA', coordinates: [-75.3705, 40.6259], type: 'DC' },
  '6058': { name: 'DC 6058', city: 'Sharon Springs, NY', coordinates: [-74.6168, 42.7992], type: 'DC' },
  '7030': { name: 'DC 7030', city: 'Grandview, WA', coordinates: [-119.9017, 46.2543], type: 'DC' },
  '6032': { name: 'DC 6032', city: 'Chino, CA', coordinates: [-117.6889, 34.0122], type: 'DC' },
  '6057': { name: 'DC 6057', city: 'Johnstown, NY', coordinates: [-74.3668, 43.0070], type: 'DC' },
};

// Mode colors
export const MODE_COLORS = {
  TL: '#3b82f6',      // Blue for Truck Load
  Rail: '#f59e0b',    // Amber for Rail
  Fleet: '#22c55e',   // Green for Fleet/LTL
  LTL: '#8b5cf6',     // Purple for LTL
  Intermodal: '#ec4899', // Pink for Intermodal
};

// Destination type colors
export const TYPE_COLORS = {
  DC: '#06b6d4',      // Cyan
  CP: '#8b5cf6',      // Purple
  Store: '#f97316',   // Orange
  RDC: '#10b981',     // Green for Regional DC
};

// Origin colors for warehouses
const ORIGIN_COLORS = [
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
  '#f59e0b', // Amber
  '#10b981', // Green
  '#ef4444', // Red
  '#06b6d4', // Cyan
];

const TransitTimeMap = memo(({
  shipPoints = [],           // From tenant Settings
  transitRoutes = [],        // Routes data (origin -> dest with days/mode)
  destinations = [],         // Destination locations
  selectedDestination,
  onSelectDestination,
  selectedOrigin,
  onSelectOrigin,
  selectedMode,
  height = 400
}) => {

  // Convert ship points to map origins with coordinates
  const origins = useMemo(() => {
    const result = {};
    shipPoints.forEach((sp, idx) => {
      const location = sp.location?.toUpperCase() || '';
      const coords = LOCATION_COORDINATES[location];
      if (coords) {
        result[sp.code] = {
          code: sp.code,
          name: sp.name || sp.code,
          location: sp.location,
          city: `${sp.city || ''}, ${sp.state || ''}`.trim().replace(/^,\s*|,\s*$/g, '') || location,
          coordinates: coords,
          color: ORIGIN_COLORS[idx % ORIGIN_COLORS.length],
          active: sp.active !== false
        };
      }
    });
    return result;
  }, [shipPoints]);

  // Use provided destinations or default Walmart DCs
  const mapDestinations = useMemo(() => {
    if (destinations.length > 0) return destinations;
    // Default: use common Walmart DCs
    return Object.entries(WALMART_DCS).map(([id, dc]) => ({
      id: `dc${id}`,
      ...dc
    }));
  }, [destinations]);

  // Generate sample routes if none provided
  const routes = useMemo(() => {
    if (transitRoutes.length > 0) return transitRoutes;

    // Generate sample routes from each active origin to destinations
    const sampleRoutes = [];
    const modes = ['TL', 'Rail', 'Fleet'];
    const originCodes = Object.keys(origins);

    mapDestinations.forEach(dest => {
      // Each destination gets routes from 1-3 random origins
      const numRoutes = Math.min(originCodes.length, Math.floor(Math.random() * 3) + 1);
      const shuffledOrigins = [...originCodes].sort(() => Math.random() - 0.5);

      for (let i = 0; i < numRoutes; i++) {
        const originCode = shuffledOrigins[i];
        if (!originCode) continue;

        sampleRoutes.push({
          origin: originCode,
          destId: dest.id,
          days: Math.floor(Math.random() * 5) + 1,
          mode: modes[Math.floor(Math.random() * modes.length)]
        });
      }
    });

    return sampleRoutes;
  }, [transitRoutes, origins, mapDestinations]);

  // Get routes for selected destination
  const getRoutesToDest = (destId) => {
    let filtered = routes.filter(r => r.destId === destId);
    if (selectedOrigin) {
      filtered = filtered.filter(r => r.origin === selectedOrigin);
    }
    if (selectedMode) {
      filtered = filtered.filter(r => r.mode === selectedMode);
    }
    return filtered;
  };

  // Get destination by ID
  const getDestById = (destId) => mapDestinations.find(d => d.id === destId);

  // Filter destinations based on selected origin
  const getVisibleDestinations = () => {
    if (!selectedOrigin) return mapDestinations;
    const destIds = new Set(routes.filter(r => r.origin === selectedOrigin).map(r => r.destId));
    return mapDestinations.filter(d => destIds.has(d.id));
  };

  // Get routes count per destination (for showing multi-origin destinations)
  const getRouteCountForDest = (destId) => {
    return routes.filter(r => r.destId === destId).length;
  };

  const visibleDestinations = getVisibleDestinations();
  const activeRoutes = selectedDestination ? getRoutesToDest(selectedDestination) : [];
  const hasOrigins = Object.keys(origins).length > 0;

  return (
    <div className="relative">
      {!hasOrigins && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100/80 z-10 rounded-lg">
          <div className="text-center p-6">
            <div className="text-slate-400 text-lg mb-2">No Ship Points Configured</div>
            <div className="text-slate-500 text-sm">Add ship points in Settings to see them on the map</div>
          </div>
        </div>
      )}

      <ComposableMap
        projection="geoAlbersUsa"
        style={{ width: '100%', height: `${height}px` }}
      >
        {/* US States */}
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#e2e8f0"
                stroke="#cbd5e1"
                strokeWidth={0.5}
                style={{
                  default: { outline: 'none' },
                  hover: { fill: '#cbd5e1', outline: 'none' },
                  pressed: { outline: 'none' },
                }}
              />
            ))
          }
        </Geographies>

        {/* Transit Lines - draw when destination is selected */}
        {activeRoutes.map((route, idx) => {
          const origin = origins[route.origin];
          const dest = getDestById(route.destId);
          if (!origin || !dest) return null;

          return (
            <Line
              key={`route-${idx}`}
              from={origin.coordinates}
              to={dest.coordinates}
              stroke={MODE_COLORS[route.mode] || '#3b82f6'}
              strokeWidth={3}
              strokeLinecap="round"
              style={{
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }}
            />
          );
        })}

        {/* Origin Markers (Ship Points from Settings) */}
        {Object.entries(origins).map(([code, origin]) => {
          if (!origin.active) return null;
          const isSelected = selectedOrigin === code;
          const hasRoutesToSelected = selectedDestination &&
            routes.some(r => r.origin === code && r.destId === selectedDestination);

          return (
            <Marker
              key={code}
              coordinates={origin.coordinates}
              onClick={() => onSelectOrigin && onSelectOrigin(isSelected ? null : code)}
              style={{ cursor: 'pointer' }}
            >
              {/* Outer glow for selected or connected */}
              {(isSelected || hasRoutesToSelected) && (
                <circle r={14} fill={origin.color} opacity={0.3} />
              )}
              {/* Main marker */}
              <circle
                r={10}
                fill={origin.color}
                stroke="white"
                strokeWidth={2}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                }}
              />
              {/* Label */}
              <text
                textAnchor="middle"
                y={-15}
                style={{
                  fontFamily: 'system-ui',
                  fontSize: '10px',
                  fontWeight: 600,
                  fill: origin.color,
                  textShadow: '0 0 3px white, 0 0 3px white'
                }}
              >
                {origin.location || code.substring(0, 3)}
              </text>
            </Marker>
          );
        })}

        {/* Destination Markers */}
        {visibleDestinations.map((dest) => {
          const isSelected = selectedDestination === dest.id;
          const typeColor = TYPE_COLORS[dest.type] || '#64748b';
          const routeCount = getRouteCountForDest(dest.id);

          return (
            <Marker
              key={dest.id}
              coordinates={dest.coordinates}
              onClick={() => onSelectDestination && onSelectDestination(isSelected ? null : dest.id)}
              style={{ cursor: 'pointer' }}
            >
              {/* Selection ring */}
              {isSelected && (
                <circle
                  r={16}
                  fill="none"
                  stroke={typeColor}
                  strokeWidth={2}
                  opacity={0.6}
                />
              )}
              {/* Main marker - square for destinations */}
              <rect
                x={-7}
                y={-7}
                width={14}
                height={14}
                rx={3}
                fill={isSelected ? typeColor : 'white'}
                stroke={typeColor}
                strokeWidth={2}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }}
              />
              {/* Route count badge (if multiple origins serve this dest) */}
              {routeCount > 1 && !isSelected && (
                <g>
                  <circle cx={8} cy={-8} r={7} fill="#ef4444" stroke="white" strokeWidth={1} />
                  <text x={8} y={-5} textAnchor="middle" fontSize="8" fontWeight="bold" fill="white">
                    {routeCount}
                  </text>
                </g>
              )}
              {/* Label */}
              <text
                textAnchor="middle"
                y={22}
                style={{
                  fontFamily: 'system-ui',
                  fontSize: '9px',
                  fontWeight: isSelected ? 600 : 400,
                  fill: isSelected ? typeColor : '#64748b',
                  textShadow: '0 0 3px white, 0 0 3px white'
                }}
              >
                {dest.name}
              </text>
            </Marker>
          );
        })}
      </ComposableMap>

      {/* Selected Route Info Overlay */}
      {selectedDestination && activeRoutes.length > 0 && (
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg border border-slate-200 p-4 max-w-xs">
          <div className="font-semibold text-slate-800 mb-1">
            {getDestById(selectedDestination)?.name}
          </div>
          <div className="text-xs text-slate-500 mb-3">
            {getDestById(selectedDestination)?.city}
          </div>
          <div className="text-xs text-slate-400 mb-2">
            {activeRoutes.length} route{activeRoutes.length > 1 ? 's' : ''} from {new Set(activeRoutes.map(r => r.origin)).size} warehouse{new Set(activeRoutes.map(r => r.origin)).size > 1 ? 's' : ''}
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {activeRoutes.map((route, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: origins[route.origin]?.color }}
                  />
                  <span className="text-slate-600">{origins[route.origin]?.location || route.origin}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: `${MODE_COLORS[route.mode]}20`,
                      color: MODE_COLORS[route.mode]
                    }}
                  >
                    {route.mode}
                  </span>
                  <span className="font-semibold text-slate-800">{route.days}d</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      {!selectedDestination && !selectedOrigin && hasOrigins && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-4 py-2 rounded-lg text-sm text-slate-500 shadow">
          Click a warehouse (circle) or destination (square) to see routes
        </div>
      )}
    </div>
  );
});

TransitTimeMap.displayName = 'TransitTimeMap';

export default TransitTimeMap;
